/**
 * Starborn Calendar - Client-side utility for calendar links
 *
 * No dependencies. Use from any app that needs "Add to Google Calendar" / ICS.
 *
 * Setup:
 *   <script src="https://starborn.app/__starborn/calendar.js"></script>
 *
 * Usage:
 *   var links = starbornCalendar.getCalendarLinks({
 *     title: 'My Event',
 *     start: '2027-01-02T03:00:00Z',  // Date or ISO string
 *     end: '2027-01-02T05:00:00Z',    // optional; defaults to start + 1h
 *     // Optional timezone-aware local fields:
 *     // timeZone: 'America/Los_Angeles',
 *     // startLocal: '2027-01-01T19:00:00',
 *     // endLocal: '2027-01-01T21:00:00',
 *     location: 'Venue Name',
 *     description: 'Event details'
 *   });
 *   // links.google, links.icsDataUrl, links.icsFilename
 */

(function() {
  'use strict';

  function toDate(val) {
    if (val instanceof Date) return val;
    if (typeof val === 'string') return new Date(val);
    return null;
  }

  function pad(num) {
    return String(num).padStart(2, '0');
  }

  function parseLocalDateTime(str) {
    if (typeof str !== 'string') return null;
    var m = str.trim().match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!m) return null;
    return {
      year: Number(m[1]),
      month: Number(m[2]),
      day: Number(m[3]),
      hour: Number(m[4]),
      minute: Number(m[5]),
      second: Number(m[6] || '0')
    };
  }

  function isValidTimeZone(timeZone) {
    if (!timeZone) return false;
    try {
      Intl.DateTimeFormat('en-US', { timeZone: timeZone }).format(new Date());
      return true;
    } catch {
      return false;
    }
  }

  function getPartsInTimeZone(date, timeZone) {
    var formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    var out = {};
    formatter.formatToParts(date).forEach(function(part) {
      if (part.type !== 'literal') out[part.type] = part.value;
    });
    return {
      year: Number(out.year),
      month: Number(out.month),
      day: Number(out.day),
      hour: Number(out.hour),
      minute: Number(out.minute),
      second: Number(out.second)
    };
  }

  function getTimeZoneOffsetMillis(date, timeZone) {
    var parts = getPartsInTimeZone(date, timeZone);
    var asUtcMillis = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return asUtcMillis - date.getTime();
  }

  function zonedLocalToDate(parts, timeZone) {
    var utcGuess = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    for (var i = 0; i < 4; i++) {
      var offset = getTimeZoneOffsetMillis(new Date(utcGuess), timeZone);
      var adjusted = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - offset;
      if (adjusted === utcGuess) break;
      utcGuess = adjusted;
    }
    return new Date(utcGuess);
  }

  function formatLocalIcsDate(parts) {
    return (
      String(parts.year) +
      pad(parts.month) +
      pad(parts.day) +
      'T' +
      pad(parts.hour) +
      pad(parts.minute) +
      pad(parts.second)
    );
  }

  function formatGoogleDate(d) {
    var y = d.getUTCFullYear();
    var m = String(d.getUTCMonth() + 1).padStart(2, '0');
    var day = String(d.getUTCDate()).padStart(2, '0');
    var h = String(d.getUTCHours()).padStart(2, '0');
    var min = String(d.getUTCMinutes()).padStart(2, '0');
    var s = String(d.getUTCSeconds()).padStart(2, '0');
    return y + m + day + 'T' + h + min + s + 'Z';
  }

  function getCalendarLinks(event) {
    var title = event.title || '';
    var timeZone = typeof event.timeZone === 'string' ? event.timeZone.trim() : '';
    var hasValidTimeZone = isValidTimeZone(timeZone);
    var startLocalParts = parseLocalDateTime(event.startLocal);
    var endLocalParts = parseLocalDateTime(event.endLocal);

    var start = null;
    if (hasValidTimeZone && startLocalParts) {
      start = zonedLocalToDate(startLocalParts, timeZone);
    } else {
      start = toDate(event.start);
    }

    var end = null;
    if (hasValidTimeZone && endLocalParts) {
      end = zonedLocalToDate(endLocalParts, timeZone);
    } else if (event.end != null) {
      end = toDate(event.end);
    }

    if (!start) return { google: '', icsDataUrl: '', icsFilename: 'invite.ics' };
    if (!end) end = new Date(start.getTime() + 60 * 60 * 1000);
    var location = event.location || '';
    var description = event.description || '';

    var startStr = formatGoogleDate(start);
    var endStr = formatGoogleDate(end);
    var google = 'https://calendar.google.com/calendar/render?action=TEMPLATE&text=' + encodeURIComponent(title) +
      '&dates=' + startStr + '/' + endStr +
      '&details=' + encodeURIComponent(description) +
      '&location=' + encodeURIComponent(location);
    if (hasValidTimeZone) {
      google += '&ctz=' + encodeURIComponent(timeZone);
    }

    var icsLines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT', 'SUMMARY:' + (title || '').replace(/\n/g, '\\n')];
    if (hasValidTimeZone) {
      var startPartsForIcs = startLocalParts || getPartsInTimeZone(start, timeZone);
      var endPartsForIcs = endLocalParts || getPartsInTimeZone(end, timeZone);
      icsLines.push('DTSTART;TZID=' + timeZone + ':' + formatLocalIcsDate(startPartsForIcs));
      icsLines.push('DTEND;TZID=' + timeZone + ':' + formatLocalIcsDate(endPartsForIcs));
    } else {
      icsLines.push('DTSTART:' + startStr);
      icsLines.push('DTEND:' + endStr);
    }
    icsLines.push(
      'LOCATION:' + (location || '').replace(/\n/g, '\\n'),
      'DESCRIPTION:' + (description || '').replace(/\n/g, '\\n'),
      'END:VEVENT',
      'END:VCALENDAR'
    );
    var ics = icsLines.join('\r\n');
    var icsDataUrl = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(ics);
    var icsFilename = 'invite.ics';

    return { google: google, icsDataUrl: icsDataUrl, icsFilename: icsFilename };
  }

  window.starbornCalendar = {
    getCalendarLinks: getCalendarLinks
  };
})();
